import { dlog } from '../utils/debug';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInAnonymously,
  linkWithCredential,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { ref, set, get, query, orderByChild, equalTo, update } from 'firebase/database';
import { isFeatureEnabled } from '../config/features';

const AuthContext = createContext(null);

// Transfer ownership of matches created under an anonymous UID to a real
// UID. Used after linking or after a sign-in conflict during link.
// Runs a single batched update; safe to call when no matches need moving.
async function migrateAnonMatches(oldUid, newUid) {
  if (!oldUid || !newUid || oldUid === newUid) return;
  try {
    const matchesRef = ref(db, 'matches');
    const q = query(matchesRef, orderByChild('creatorId'), equalTo(oldUid));
    const snapshot = await get(q);
    if (!snapshot.exists()) {
      dlog(`ℹ️ No anon matches to migrate from ${oldUid}`);
      return;
    }
    const updates = {};
    snapshot.forEach((child) => {
      updates[`matches/${child.key}/creatorId`] = newUid;
    });
    await update(ref(db), updates);
    dlog(`✅ Migrated ${Object.keys(updates).length} anon match(es) ${oldUid} → ${newUid}`);
  } catch (error) {
    console.error('❌ Error migrating anon matches:', error);
    // Don't throw — migration failure shouldn't block sign-in.
  }
}

// Helper function to migrate guest matches to Firebase
async function migrateGuestMatches(userId) {
  try {
    const guestMatches = JSON.parse(localStorage.getItem('guestViewedMatches') || '{}');
    const matchIds = Object.keys(guestMatches);
    
    if (matchIds.length === 0) {
      dlog('ℹ️ No guest matches to migrate');
      return;
    }
    
    dlog(`🔄 Migrating ${matchIds.length} guest matches to Firebase...`);
    
    // Save each guest match to Firebase
    for (const matchId of matchIds) {
      const viewedRef = ref(db, `users/${userId}/viewedMatches/${matchId}`);
      await set(viewedRef, guestMatches[matchId]);
    }
    
    // Clear localStorage after successful migration
    localStorage.removeItem('guestViewedMatches');
    dlog('✅ Successfully migrated guest matches and cleared localStorage');
    
    // Dispatch custom event to notify components that migration is complete
    window.dispatchEvent(new CustomEvent('guestMatchesMigrated', { 
      detail: { matchIds } 
    }));
    
  } catch (error) {
    console.error('❌ Error migrating guest matches:', error);
    // Don't throw - migration failure shouldn't break sign-in
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [loading, setLoading] = useState(true);
  // Concurrent calls to ensureAnonymousUser must share a single in-flight
  // sign-in — otherwise multiple gestures racing during cold load could
  // each create their own anon account.
  const anonInflightRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Lazily sign the user in anonymously. Idempotent — safe to call from
  // any gesture handler that needs a stable UID for an upcoming write.
  // Returns the resulting Firebase user. Inert (returns existing user)
  // when ANONYMOUS_SCORING flag is off.
  const ensureAnonymousUser = async () => {
    if (auth.currentUser) return auth.currentUser;
    if (!isFeatureEnabled('ANONYMOUS_SCORING', null)) return null;
    if (anonInflightRef.current) return anonInflightRef.current;
    anonInflightRef.current = (async () => {
      try {
        const credential = await signInAnonymously(auth);
        dlog(`🕵️  Anonymous user created: ${credential.user.uid}`);
        return credential.user;
      } catch (error) {
        console.error('Anonymous sign-in failed:', error);
        return null;
      } finally {
        anonInflightRef.current = null;
      }
    })();
    return anonInflightRef.current;
  };

  // Shared post-sign-in profile + migration work for any real-account
  // sign-in path. If the previous user was anonymous, transfer their
  // matches to the new UID. Idempotent on user profile creation.
  const finalizeSignIn = async ({ realUser, anonUid, provider }) => {
    // User has signed in for real — clear the "I'm intentionally
    // signed out" marker so the next visit / state change goes
    // through the normal auto-redirect flow again.
    sessionStorage.removeItem('wasSignedOut');
    const userRef = ref(db, `users/${realUser.uid}`);
    const snapshot = await get(userRef);
    if (!snapshot.exists()) {
      await set(userRef, {
        email: realUser.email,
        displayName: realUser.displayName ?? null,
        photoURL: realUser.photoURL ?? null,
        createdAt: Date.now(),
        provider,
      });
    }
    // Anon → real migration. No-op when anonUid is unset or already === realUid.
    if (anonUid && anonUid !== realUser.uid) {
      await migrateAnonMatches(anonUid, realUser.uid);
    }
    // Existing guest-viewed-matches localStorage migration is preserved.
    await migrateGuestMatches(realUser.uid);
  };

  // Google Sign-In
  //
  // Earlier version tried linkWithPopup first to preserve the anon UID,
  // then fell back to signInWithPopup on auth/credential-already-in-use.
  // That two-popup sequence breaks on browsers that aggressively block
  // popups outside the original user-gesture context (Safari, often
  // Chrome) — the second popup is silently blocked and sign-in fails
  // with no visible error.
  //
  // Single-popup flow now: always signInWithPopup. If the user was
  // anonymous, capture their UID up front and run the anon-matches
  // migration via finalizeSignIn after a successful sign-in.
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const wasAnon = auth.currentUser?.isAnonymous;
    const anonUid = wasAnon ? auth.currentUser.uid : null;
    try {
      const result = await signInWithPopup(auth, provider);

      await finalizeSignIn({
        realUser: result.user,
        anonUid,
        provider: 'google',
      });

      return { success: true };
    } catch (error) {
      console.error('Google sign-in error:', error);
      // Silent cancel — user closed the popup themselves.
      if (error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request') {
        return { success: false, error: null };
      }
      return {
        success: false,
        error: getAuthErrorMessage(error.code)
      };
    }
  };

  // Email/Password Sign Up
  const signUpWithEmail = async (email, password, displayName) => {
    const wasAnon = auth.currentUser?.isAnonymous;
    try {
      let result;
      if (wasAnon) {
        // Upgrade anonymous account in place. Preserves UID, so matches
        // created while anonymous stay attached. If the email is already
        // registered to another account, surface a clean error (user
        // should sign in, not sign up).
        const credential = EmailAuthProvider.credential(email, password);
        result = await linkWithCredential(auth.currentUser, credential);
      } else {
        result = await createUserWithEmailAndPassword(auth, email, password);
      }

      // Update display name
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      // Send verification email
      await sendEmailVerification(result.user);

      // Create user profile in database
      await set(ref(db, `users/${result.user.uid}`), {
        email: result.user.email,
        displayName: displayName || null,
        createdAt: Date.now(),
        provider: 'email',
        emailVerified: false,
      });

      // Migrate guest viewed matches to Firebase
      await migrateGuestMatches(result.user.uid);

      return {
        success: true,
        message: 'Account created! Please check your email to verify your account.'
      };
    } catch (error) {
      console.error('Sign-up error:', error);
      return {
        success: false,
        error: getAuthErrorMessage(error.code)
      };
    }
  };

  // Email/Password Sign In
  const signInWithEmail = async (email, password) => {
    const wasAnon = auth.currentUser?.isAnonymous;
    const anonUid = wasAnon ? auth.currentUser.uid : null;
    try {
      // Sign-in to an existing account always switches identities — the
      // credential is already in use, so linking can't preserve the anon
      // UID. Sign in directly, then migrate the anon UID's matches.
      const result = await signInWithEmailAndPassword(auth, email, password);

      if (anonUid && anonUid !== result.user.uid) {
        await migrateAnonMatches(anonUid, result.user.uid);
      }
      await migrateGuestMatches(result.user.uid);

      return { success: true };
    } catch (error) {
      console.error('Sign-in error:', error);
      return {
        success: false,
        error: getAuthErrorMessage(error.code)
      };
    }
  };

  // Sign Out
  //
  // Marks `wasSignedOut` in sessionStorage so the LandingPage's
  // auto-redirect-into-new-match flow doesn't immediately reclaim
  // the user as anonymous + create a fresh match — that produced
  // a loop where "Sign Out" actually felt like "Score a new match."
  // The flag clears in finalizeSignIn when the user signs back in.
  const signOut = async () => {
    try {
      sessionStorage.setItem('wasSignedOut', '1');
      await firebaseSignOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Sign-out error:', error);
      return {
        success: false,
        error: 'Failed to sign out. Please try again.'
      };
    }
  };

  // Password Reset
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { 
        success: true, 
        message: 'Password reset email sent! Check your inbox.' 
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: getAuthErrorMessage(error.code) 
      };
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    try {
      if (!auth.currentUser) {
        return { success: false, error: 'No user signed in' };
      }
      await sendEmailVerification(auth.currentUser);
      return { 
        success: true, 
        message: 'Verification email sent! Check your inbox.' 
      };
    } catch (error) {
      console.error('Verification email error:', error);
      return { 
        success: false, 
        error: 'Failed to send verification email. Please try again.' 
      };
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    resetPassword,
    resendVerificationEmail,
    // Anonymous-scoring scaffolding (behind ANONYMOUS_SCORING flag).
    // Lazy: call ensureAnonymousUser() from a gesture handler that needs
    // a stable UID for an upcoming write (e.g., creating a match).
    ensureAnonymousUser,
    isAnonymous: !!user && user.isAnonymous,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper function to get user-friendly error messages
function getAuthErrorMessage(errorCode) {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Try signing in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password sign-in is not enabled. Please contact support.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found with this email. Please sign up first.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in cancelled. Please try again.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups for this site and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is registered with a different sign-in method. Try email/password instead.';
    case 'auth/credential-already-in-use':
      return 'This Google account is already linked to another user. Try signing in with email.';
    default:
      return 'An error occurred. Please try again.';
  }
}

